import { Controller } from "@hotwired/stimulus";

import { formatPhoneNumberForDisplay, normalizePhoneNumberToE164 } from "../lib/phone_number";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const ALL_DAYS = [...WEEKDAYS, "saturday", "sunday"];
const DEFAULT_TIMEZONE = "America/Detroit";
const SCHEDULE_PRESETS = {
  "weekdays-8-5": { days: WEEKDAYS, startTime: "08:00", endTime: "17:00" },
  "weekdays-9-5": { days: WEEKDAYS, startTime: "09:00", endTime: "17:00" },
  always: { days: ALL_DAYS, startTime: "00:00", endTime: "00:00" },
};
let recordSequence = 20;

const nextRecordKey = () => {
  recordSequence += 1;
  return recordSequence.toString();
};

export default class extends Controller {
  static targets = ["addButton", "rows", "row", "template"];
  static values = {
    defaultTimezone: String,
    maxDestinations: Number,
    maxSchedules: Number,
  };

  connect() {
    this.handleFormUpdate = this.handleFormUpdate.bind(this);
    this.element.addEventListener("input", this.handleFormUpdate);
    this.element.addEventListener("change", this.handleFormUpdate);
    this.updateAll();
    this.revealInvalidDestination();
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleFormUpdate);
    this.element.removeEventListener("change", this.handleFormUpdate);
  }

  add() {
    if (this.rowTargets.length >= this.maxDestinations) return;

    const key = nextRecordKey();
    const html = this.templateTarget.innerHTML
      .replaceAll("NEW_RECORD", key)
      .replaceAll("NEW_DESTINATION_NUMBER", String(this.rowTargets.length + 1));

    this.rowsTarget.insertAdjacentHTML("beforeend", html);
    const row = this.rowsTarget.lastElementChild;
    this.collapseOtherRows(row);
    this.setRowExpanded(row, true);
    this.updateRow(row);
    this.updateButtons();
  }

  remove(event) {
    const row = this.rowFor(event.target);
    if (!row) return;

    if (
      this.schedulesFor(row).length > 0 &&
      !window.confirm("Remove this destination and its availability windows?")
    ) {
      return;
    }

    row.remove();
    this.updateAll();
  }

  toggle(event) {
    const row = this.rowFor(event.currentTarget);
    if (!row) return;

    const toggle = this.findTarget(row, "toggle");
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    if (!expanded) this.collapseOtherRows(row);
    this.setRowExpanded(row, !expanded);
  }

  addSchedule(event) {
    const row = this.rowFor(event.currentTarget);
    if (!row || this.schedulesFor(row).length >= this.maxSchedules) return;

    const schedule = this.insertSchedule(row);
    if (!schedule) return;

    this.applyScheduleState(schedule, {
      ...SCHEDULE_PRESETS["weekdays-9-5"],
      timezone: this.timezoneForNewSchedule(),
    });
    this.updateRow(row);
  }

  applyPreset(event) {
    const select = event.currentTarget;
    const schedule = select.closest("[data-call-group-destinations-target~='schedule']");
    const row = this.rowFor(select);
    const preset = SCHEDULE_PRESETS[select.value];
    if (!schedule || !row || !preset) return;

    this.applyScheduleState(schedule, {
      ...preset,
      timezone: this.fieldValue(schedule, "timezone"),
    });
    select.value = "";
    this.updateRow(row);
  }

  duplicateSchedule(event) {
    const source = event.currentTarget.closest("[data-call-group-destinations-target~='schedule']");
    const row = this.rowFor(event.currentTarget);
    if (!source || !row || this.schedulesFor(row).length >= this.maxSchedules) return;

    const duplicate = this.insertSchedule(row, source);
    if (!duplicate) return;

    this.applyScheduleState(duplicate, this.scheduleState(source));
    this.updateRow(row);
  }

  removeSchedule(event) {
    const schedule = event.currentTarget.closest(
      "[data-call-group-destinations-target~='schedule']",
    );
    const row = this.rowFor(event.currentTarget);
    if (!schedule || !row) return;

    schedule.remove();
    this.updateRow(row);
  }

  handleFormUpdate(event) {
    const row = this.rowFor(event.target);
    if (row && event.target.matches("[data-call-group-destinations-target~='phone']")) {
      event.target.setCustomValidity("");
    }
    if (row) this.updateRow(row);
  }

  formatPhone(event) {
    const input = event.currentTarget;
    const normalized = normalizePhoneNumberToE164(input.value);

    if (!input.value.trim() || normalized) {
      input.setCustomValidity("");
      if (normalized) input.value = formatPhoneNumberForDisplay(normalized);
    } else {
      input.setCustomValidity(
        "Enter a valid US phone number, such as (555) 123-4567, or an international number beginning with +.",
      );
    }

    this.updateRow(this.rowFor(input));
  }

  insertSchedule(row, afterSchedule = null) {
    const schedules = this.schedulesFor(row);
    const template = this.findTarget(row, "scheduleTemplate");
    const container = this.findTarget(row, "schedules");
    const key = nextRecordKey();
    const html = template.innerHTML
      .replaceAll("NEW_SCHEDULE", key)
      .replaceAll("NEW_SCHEDULE_NUMBER", String(schedules.length + 1));

    if (afterSchedule) {
      afterSchedule.insertAdjacentHTML("afterend", html);
      return afterSchedule.nextElementSibling;
    }

    container.insertAdjacentHTML("beforeend", html);
    return this.schedulesFor(row).at(-1);
  }

  scheduleState(schedule) {
    return {
      days: this.checkedDays(schedule),
      startTime: this.fieldValue(schedule, "startTime"),
      endTime: this.fieldValue(schedule, "endTime"),
      timezone: this.fieldValue(schedule, "timezone"),
    };
  }

  applyScheduleState(schedule, state) {
    schedule.querySelectorAll("input[type='checkbox'][name$='[days][]']").forEach((input) => {
      input.checked = state.days.includes(input.value);
    });
    this.setFieldValue(schedule, "startTime", state.startTime);
    this.setFieldValue(schedule, "endTime", state.endTime);
    this.setFieldValue(schedule, "timezone", state.timezone);
  }

  updateAll() {
    this.rowTargets.forEach((row) => this.updateRow(row));
    this.updateButtons();
  }

  revealInvalidDestination() {
    const invalidFocus = this.element.querySelector(
      "[data-call-group-destinations-invalid-focus='true']",
    );
    const row = this.rowFor(invalidFocus);
    if (!invalidFocus || !row) return;

    this.collapseOtherRows(row);
    this.setRowExpanded(row, true);
    invalidFocus.focus();
  }

  collapseOtherRows(activeRow) {
    this.rowTargets.forEach((row) => {
      if (row !== activeRow) this.setRowExpanded(row, false);
    });
  }

  setRowExpanded(row, expanded) {
    const toggle = this.findTarget(row, "toggle");
    const content = this.findTarget(row, "content");
    if (!toggle || !content) return;

    toggle.setAttribute("aria-expanded", String(expanded));
    content.hidden = !expanded;
  }

  updateRow(row) {
    if (!row) return;

    this.updateIdentity(row);
    this.updateSchedules(row);
    this.updateSummary(row);
    this.updateButtons();
  }

  updateIdentity(row) {
    const label = this.fieldValue(row, "label").trim();
    const phone = this.fieldValue(row, "phone").trim();
    const enabled = this.findTarget(row, "enabled").checked;
    const status = this.findTarget(row, "enabledStatus");

    this.findTarget(row, "summaryLabel").textContent = label || "New destination";
    this.findTarget(row, "summaryPhone").textContent =
      this.displayPhone(phone) || "Phone number not set";
    status.textContent = enabled ? "Enabled" : "Disabled";
    status.setAttribute("aria-label", `Destination status: ${status.textContent}`);

    row
      .querySelector("[data-call-group-destinations-target~='addScheduleButton']")
      ?.setAttribute("aria-label", `Add availability window for ${label || "new destination"}`);
  }

  updateSchedules(row) {
    const schedules = this.schedulesFor(row);

    schedules.forEach((schedule, index) => {
      const number = index + 1;
      schedule.querySelector("legend").textContent = `Availability window ${number}`;
      schedule
        .querySelector("[data-action~='call-group-destinations#duplicateSchedule']")
        .setAttribute("aria-label", `Duplicate availability window ${number}`);
      schedule
        .querySelector("[data-action~='call-group-destinations#removeSchedule']")
        .setAttribute("aria-label", `Remove availability window ${number}`);

      const days = this.checkedDays(schedule);
      this.findTarget(schedule, "inactiveScheduleWarning").hidden = days.length > 0;
      this.findTarget(schedule, "scheduleGuidance").textContent = this.scheduleGuidance(schedule);
    });

    row
      .querySelectorAll("[data-call-group-destinations-target~='addScheduleButton']")
      .forEach((button) => {
        button.disabled = schedules.length >= this.maxSchedules;
      });
  }

  updateSummary(row) {
    const summary = this.findTarget(row, "availabilitySummary");
    const schedules = this.schedulesFor(row);
    summary.replaceChildren();

    if (schedules.length === 0) {
      const warning = document.createElement("p");
      warning.dataset.callGroupDestinationsTarget = "noSchedulesWarning";
      warning.textContent = "No scheduled hours—this destination will not receive calls.";
      summary.append(warning);
      return;
    }

    const visibleSchedules = schedules.slice(0, 3);
    const timezones = visibleSchedules.map((schedule) =>
      this.fieldValue(schedule, "timezone").trim(),
    );
    const timezoneLabels = visibleSchedules.map((schedule) => this.timezoneLabel(schedule));
    const sharedTimezone = timezones[0] && timezones.every((timezone) => timezone === timezones[0]);

    visibleSchedules.forEach((schedule, index) => {
      const line = document.createElement("p");
      const timezone = sharedTimezone ? "" : timezoneLabels[index] || "Timezone not set";
      line.textContent = `${this.scheduleSummary(schedule)}${timezone ? ` ${timezone}` : ""}`;
      summary.append(line);
    });

    if (sharedTimezone) {
      const timezoneLine = document.createElement("p");
      timezoneLine.textContent = timezoneLabels[0];
      summary.append(timezoneLine);
    }

    if (schedules.length > 3) {
      const more = document.createElement("p");
      more.textContent = `+ ${schedules.length - 3} more availability windows`;
      summary.append(more);
    }
  }

  scheduleSummary(schedule) {
    const days = this.checkedDays(schedule).map((day) => day[0].toUpperCase() + day.slice(1, 3));
    const startTime = this.fieldValue(schedule, "startTime");
    const endTime = this.fieldValue(schedule, "endTime");
    let timing = "";

    if (startTime && endTime && startTime === endTime) timing = " (24 hours)";
    if (startTime && endTime && startTime > endTime) timing = " (overnight)";

    return `${days.length > 0 ? days.join(", ") : "No days selected"}: ${startTime || "--:--"}–${endTime || "--:--"}${timing}`;
  }

  timezoneLabel(schedule) {
    const select = this.findTarget(schedule, "timezone");
    return select?.selectedOptions[0]?.textContent.trim() || select?.value || "";
  }

  scheduleGuidance(schedule) {
    const startTime = this.fieldValue(schedule, "startTime");
    const endTime = this.fieldValue(schedule, "endTime");

    if (startTime && startTime === endTime) {
      return "Matching start and end times provide 24-hour availability on the selected days.";
    }
    if (startTime && endTime && startTime > endTime) {
      return "This availability window continues overnight into the following day.";
    }
    return "Availability applies between the selected start and end times on the selected days.";
  }

  updateButtons() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = this.rowTargets.length >= this.maxDestinations;
    }

    this.rowTargets.forEach((row, index) => {
      const remove = row.querySelector("[data-action~='call-group-destinations#remove']");
      if (remove) remove.setAttribute("aria-label", `Remove destination ${index + 1}`);
    });
  }

  timezoneForNewSchedule() {
    const configured = this.hasDefaultTimezoneValue ? this.defaultTimezoneValue : "";
    return configured || DEFAULT_TIMEZONE;
  }

  schedulesFor(row) {
    const container = this.findTarget(row, "schedules");
    return container
      ? Array.from(container.children).filter((child) => child.matches(".call-group-schedule"))
      : [];
  }

  checkedDays(schedule) {
    return Array.from(
      schedule.querySelectorAll("input[type='checkbox'][name$='[days][]']:checked"),
      (input) => input.value,
    );
  }

  fieldValue(container, field) {
    return container.querySelector(`[name$='[${field}]']`)?.value || "";
  }

  setFieldValue(container, field, value) {
    const input = container.querySelector(`[name$='[${field}]']`);
    if (input) input.value = value;
  }

  findTarget(container, target) {
    return container.querySelector(`[data-call-group-destinations-target~='${target}']`);
  }

  rowFor(element) {
    return element?.closest("[data-call-group-destinations-target~='row']");
  }

  displayPhone(phone) {
    return formatPhoneNumberForDisplay(phone);
  }

  get maxDestinations() {
    return this.hasMaxDestinationsValue ? this.maxDestinationsValue : 10;
  }

  get maxSchedules() {
    return this.hasMaxSchedulesValue ? this.maxSchedulesValue : 20;
  }
}
